import React, { ReactNode, useRef } from 'react'
import { Sparkles, User, LogIn, History, MessageCircle, Sliders, Send, Eye, Paperclip, X, Image as ImageIcon, Gift, Ticket, Zap, Search } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import OpenAIStatus from '../OpenAIStatus'
import GenerationCounter from '../GenerationCounter'
import FootIcon from '../icons/FootIcon'

interface DesktopLayoutProps {
  children: ReactNode
  onShowAuthModal: () => void
  onShowProfile: () => void
  onShowCreate: () => void
  onShowHistory: () => void
  onShowSavedFeet: () => void
  onShowDiscover: () => void
  onShowGift?: () => void
  onShowRedemption?: () => void
  onShowGlobalSearch?: () => void
  activeTab?: 'create' | 'history' | 'savedFeet' | 'discover' | null
  authMode: 'signin' | 'signup'
  setAuthMode: (mode: 'signin' | 'signup') => void
  showFilters?: boolean
  onToggleFilters?: () => void
  prompt?: string
  onPromptChange?: (value: string) => void
  onGenerate?: () => void
  isGenerating?: boolean
  filterSection?: ReactNode
  discoverSection?: ReactNode
  canGenerate?: boolean
  uploadedImages?: File[]
  onImagesChange?: (images: File[]) => void
}

const DesktopLayout: React.FC<DesktopLayoutProps> = ({
  children,
  onShowAuthModal,
  onShowProfile,
  onShowCreate,
  onShowHistory,
  onShowSavedFeet,
  onShowDiscover,
  onShowGift,
  onShowRedemption,
  onShowGlobalSearch,
  activeTab = 'create',
  setAuthMode,
  showFilters = false,
  onToggleFilters,
  prompt = '',
  onPromptChange,
  onGenerate,
  isGenerating = false,
  filterSection,
  discoverSection,
  canGenerate = true,
  uploadedImages = [],
  onImagesChange
}) => {
  const { user, profile, loading } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (files: FileList | null) => {
    if (!files || !onImagesChange) return
    
    const imageFiles = Array.from(files).filter(file => 
      file.type.startsWith('image/')
    )
    
    if (imageFiles.length > 0) {
      const newImages = [...uploadedImages, ...imageFiles].slice(0, 3) // Max 3 images
      onImagesChange(newImages)
    }
  }

  const handleAttachClick = () => {
    if (!canGenerate) return
    fileInputRef.current?.click()
  }

  const handleRemoveImage = (index: number) => {
    if (!onImagesChange) return
    const newImages = uploadedImages.filter((_, i) => i !== index)
    onImagesChange(newImages)
  }

  const getTabButtonClass = (tabName: string) => {
    const isActive = activeTab === tabName
    return `w-full flex items-center space-x-3 p-3 rounded-lg transition-colors text-left ${
      isActive 
        ? 'bg-primary-50 border border-primary-200 text-primary-700' 
        : 'hover:bg-gray-50 text-gray-700'
    }`
  }

  const getTabIconClass = (tabName: string) => {
    const isActive = activeTab === tabName
    return `w-5 h-5 ${isActive ? 'text-primary-500' : 'text-gray-400'}`
  }

  const getSecondaryButtonClass = () => {
    return 'w-full flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors text-left'
  }

  const getSecondaryIconClass = (color: string = 'gray') => {
    const colorClasses = {
      primary: 'text-primary-500',
      green: 'text-green-500',
      blue: 'text-blue-500',
      gray: 'text-gray-400'
    }
    return `w-5 h-5 ${colorClasses[color as keyof typeof colorClasses] || colorClasses.gray}`
  }

  // Show input bar only for Create tab and when Discover panel is not shown
  const showInputBar = activeTab === 'create' || activeTab === 'discover'

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50 flex">
      {/* Sidebar */}
      <div className="w-80 bg-white/80 backdrop-blur-sm border-r border-gray-200 shadow-lg">
        <div className="p-6 border-b border-gray-200">
          {/* Logo and Title */}
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
              <FootIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Toefetti</h1>
              <p className="text-sm text-gray-600">AI Feet Image Generator</p>
            </div>
          </div>


        </div>

        {/* Navigation */}
        <div className="p-6 space-y-4">
          {/* OpenAI Status */}
          <OpenAIStatus />

          {/* Generation Counter */}
          <GenerationCounter variant="full" />

          {/* Search Section */}
          {onShowGlobalSearch && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Search</h3>
              <button
                onClick={onShowGlobalSearch}
                className={getSecondaryButtonClass()}
              >
                <Search className={getSecondaryIconClass('blue')} />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-900">Global Search</span>
                  <p className="text-xs text-gray-500">Find users, tags, and images</p>
                </div>
              </button>
            </div>
          )}

          {/* User Section */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Account</h3>
            
            {loading ? (
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded animate-pulse mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4"></div>
                </div>
              </div>
            ) : user ? (
              <div className="space-y-2">
                <button
                  onClick={onShowProfile}
                  className={getSecondaryButtonClass()}
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {profile?.display_name || user.email}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                </button>

                {onShowGift && (
                  <button
                    onClick={onShowGift}
                    className={getSecondaryButtonClass()}
                  >
                    <Gift className={getSecondaryIconClass('primary')} />
                    <span className="text-sm text-gray-700">Gift Subscription</span>
                  </button>
                )}

                {onShowRedemption && (
                  <button
                    onClick={onShowRedemption}
                    className={getSecondaryButtonClass()}
                  >
                    <Ticket className={getSecondaryIconClass('green')} />
                    <span className="text-sm text-gray-700">Redeem</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <button
                  onClick={() => {
                    setAuthMode('signin')
                    onShowAuthModal()
                  }}
                  className="w-full flex items-center justify-center space-x-2 p-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                >
                  <LogIn className="w-4 h-4" />
                  <span className="text-sm font-medium">Sign In</span>
                </button>

                <button
                  onClick={() => {
                    setAuthMode('signup')
                    onShowAuthModal()
                  }}
                  className="w-full flex items-center justify-center space-x-2 p-3 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg transition-colors"
                >
                  <User className="w-4 h-4" />
                  <span className="text-sm font-medium">Sign Up</span>
                </button>
              </div>
            )}
          </div>

          {/* Navigation Section */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Navigation</h3>
            
            <button
              onClick={onShowCreate}
              className={getTabButtonClass('create')}
            >
              <Zap className={getTabIconClass('create')} />
              <span className="text-sm font-medium">Create</span>
            </button>

            <button
              onClick={onShowDiscover}
              className={getTabButtonClass('discover')}
            >
              <Eye className={getTabIconClass('discover')} />
              <span className="text-sm">Discover</span>
            </button>

            {user && (
              <button
                onClick={onShowHistory}
                className={getTabButtonClass('history')}
              >
                <History className={getTabIconClass('history')} />
                <span className="text-sm">Generation History</span>
              </button>
            )}

            {user && (
              <button
                onClick={onShowSavedFeet}
                className={getTabButtonClass('savedFeet')}
              >
                <MessageCircle className={getTabIconClass('savedFeet')} />
                <span className="text-sm">Saved Feet</span>
              </button>
            )}
          </div>

          {/* Stats Section */}
          {user && profile && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Stats</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-lg font-bold text-gray-900">
                    {profile.generation_count || 0}
                  </div>
                  <div className="text-xs text-gray-500">Images</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-lg font-bold text-gray-900">
                    {profile.tier === 'premium' ? 'Pro' : 'Free'}
                  </div>
                  <div className="text-xs text-gray-500">Plan</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Unified Top Bar - Always visible */}
        <div className="bg-white/60 backdrop-blur-sm border-b border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {activeTab === 'create' ? 'Generate Feet Images' : 
                 activeTab === 'discover' ? 'Discover Amazing Creations' : 
                 activeTab === 'history' ? 'Generation History' : 
                 activeTab === 'savedFeet' ? 'Saved Feet' : 'Toefetti'}
              </h2>
              <div className="text-sm text-gray-500">
                {activeTab === 'create' ? 'Created with ❤️ for feet enthusiasts' :
                 activeTab === 'discover' ? 'Explore public images from the community and create your own inspired by what you see' :
                 'Manage your content'}
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Global Search Button */}
              {onShowGlobalSearch && (
                <button
                  onClick={onShowGlobalSearch}
                  className="flex items-center space-x-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors border border-blue-200"
                  title="Search users, tags, and images"
                >
                  <Search className="w-4 h-4" />
                  <span className="text-sm font-medium">Search</span>
                </button>
              )}

              {/* Filters Button - Show for Create tab */}
              {activeTab === 'create' && onToggleFilters && (
                <button
                  onClick={onToggleFilters}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors border-2 ${
                    showFilters 
                      ? 'border-primary-500 bg-primary-50 text-primary-600' 
                      : 'border-gray-300 hover:border-gray-400 text-gray-600 bg-white'
                  }`}
                  title="Toggle Filters & Presets"
                >
                  <Sliders className="w-4 h-4" />
                  <span className="text-sm font-medium">Filters</span>
                </button>
              )}

              {user && (
                <div className="flex items-center space-x-3">
                  <div className="text-sm text-gray-600">
                    Welcome back, {profile?.display_name || 'User'}
                  </div>
                  <button
                    onClick={onShowProfile}
                    className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center"
                  >
                    <User className="w-4 h-4 text-white" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Input Bar - Only show for Create tab */}
          {activeTab === 'create' && (
            <>
              {/* Image Upload Area */}
              {uploadedImages.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <ImageIcon className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">Reference Images</span>
                    <span className="text-xs text-gray-500">({uploadedImages.length}/3)</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {uploadedImages.map((file, index) => (
                      <div key={index} className="relative group">
                        <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-100">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <button
                          onClick={() => handleRemoveImage(index)}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Input Bar */}
              <div className="flex items-center space-x-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={prompt}
                    onChange={(e) => onPromptChange?.(e.target.value)}
                    placeholder={!canGenerate 
                      ? "Sign up to continue generating images..." 
                      : uploadedImages.length > 0
                      ? "Describe how to modify your reference images... (optional)"
                      : "Describe the feet you want to see, upload reference images, or use filters..."
                    }
                    disabled={!canGenerate}
                    className={`w-full px-4 py-3 pr-20 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 transition-colors ${
                      !canGenerate 
                        ? 'bg-gray-50 border-gray-200 cursor-not-allowed' 
                        : 'focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                    }`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !isGenerating && canGenerate) {
                        onGenerate?.()
                      }
                    }}
                  />
                  
                  {/* Attachment Button */}
                  <button
                    onClick={handleAttachClick}
                    disabled={!canGenerate || uploadedImages.length >= 3}
                    className={`absolute right-12 top-1/2 transform -translate-y-1/2 p-2 rounded-lg transition-colors ${
                      !canGenerate || uploadedImages.length >= 3
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-500 hover:text-primary-600 hover:bg-primary-50'
                    }`}
                    title={uploadedImages.length >= 3 ? 'Maximum 3 images allowed' : 'Upload reference image'}
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>

                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <span className="text-xs">{prompt.length}/500</span>
                  </div>

                  {/* Hidden File Input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleFileSelect(e.target.files)}
                    className="hidden"
                  />
                </div>
                
                <button
                  onClick={onGenerate}
                  disabled={isGenerating || !canGenerate}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center space-x-2 ${
                    !canGenerate
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : isGenerating || !canGenerate
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white hover:from-primary-600 hover:to-secondary-600'
                  }`}
                >
                  {isGenerating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Generating...</span>
                    </>
                  ) : !canGenerate ? (
                    <>
                      <FootIcon className="w-4 h-4" />
                      <span>Sign Up</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Generate</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          {/* Discover Tab - Show creation tools inline */}
          {activeTab === 'discover' && discoverSection && (
            <div className="mt-4">
              <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">Create New Images</h3>
                  <button
                    onClick={onShowCreate}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    Switch to Create Mode
                  </button>
                </div>
                {discoverSection}
              </div>
            </div>
          )}
        </div>

        {/* Single Unified Content Panel */}
        <div className="flex-1">
          {/* Filters Panel - Inline when active */}
          {showFilters && activeTab === 'create' && (
            <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Customize Your Image</h3>
                <button
                  onClick={onToggleFilters}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ×
                </button>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {filterSection}
              </div>
            </div>
          )}

          {/* Main Content - All content goes here */}
          <div className="p-6">
            {/* Primary Content */}
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DesktopLayout 